import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, User, Send, Check, Home } from 'lucide-react';
import ProductSilos from './ProductSilos';
import GiftCard from './GiftCard';
import type { ProductSilo, Brand, GiftAmount } from '@/utils/types';
import { Link, useNavigate } from 'react-router-dom';
import { type Contact } from './market/contacts/types';
import ContactsSelectionDialog from './market/ContactsSelectionDialog';

const BRANDS: Brand[] = [
  { id: 'mcdonalds', name: 'McDonald\'s', logo: '/images/mcdonalds.png', silo: 'restaurants' },
  { id: 'burgerking', name: 'Burger King', logo: '/images/burgerking.png', silo: 'restaurants' },
  { id: 'wendys', name: 'Wendy\'s', logo: '/images/wendys.png', silo: 'restaurants' },
  { id: 'chickfila', name: 'Chick-fil-A', logo: '/lovable-uploads/4fd095b4-4941-485a-9297-abac0d37c069.png', silo: 'restaurants' },
  
  { id: 'dunkin', name: 'Dunkin\' Donuts', logo: '/images/dunkin.png', silo: 'beverages' },
  { id: 'starbucks', name: 'Starbucks', logo: '/images/starbucks.png', silo: 'beverages' },
  { id: 'krispy', name: 'Krispy Kreme', logo: '/images/krispy.png', silo: 'beverages' },
  { id: 'timhortons', name: 'Tim Horton\'s', logo: '/images/timhortons.png', silo: 'beverages' },
  
  { id: 'cvs', name: 'CVS', logo: '/images/cvs.png', silo: 'convenience' },
  { id: 'riteaid', name: 'Rite Aid', logo: '/images/riteaid.png', silo: 'convenience' },
  { id: 'walgreens', name: 'Walgreens', logo: '/images/walgreens.png', silo: 'convenience' },
  { id: 'walmart', name: 'Walmart', logo: '/images/walmart.png', silo: 'convenience' },
  
  { id: 'showcase', name: 'Showcase Cinemas', logo: '/images/showcase.png', silo: 'entertainment' },
  { id: 'amc', name: 'AMC Theatres', logo: '/images/amc.png', silo: 'entertainment' },
  { id: 'xbox', name: 'Xbox', logo: '/images/xbox.png', silo: 'entertainment' },
  { id: 'playstation', name: 'PlayStation', logo: '/images/playstation.png', silo: 'entertainment' },
];

interface GiverFlowProps {
  onBackToWelcome: () => void;
}

type Step = 'recipient' | 'silo' | 'brand' | 'amount' | 'checkout' | 'confirmation';

const GiverFlow: React.FC<GiverFlowProps> = ({ onBackToWelcome }) => {
  const [step, setStep] = useState<Step>('recipient');
  const [recipient, setRecipient] = useState<string>('');
  const [selectedSilo, setSelectedSilo] = useState<ProductSilo | null>(null);
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const [selectedAmount, setSelectedAmount] = useState<GiftAmount | null>(null);
  const [message, setMessage] = useState<string>('');
  const [showContactsDialog, setShowContactsDialog] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const navigate = useNavigate();
  
  const handleContinue = () => {
    if (step === 'recipient') {
      setStep('silo');
    } else if (step === 'silo' && selectedSilo) {
      setStep('brand');
    } else if (step === 'brand' && selectedBrand) {
      setStep('amount');
    } else if (step === 'amount' && selectedAmount) {
      setStep('checkout');
    } else if (step === 'checkout') {
      setStep('confirmation');
    }
  };
  
  const handleBack = () => {
    if (step === 'silo') {
      setStep('recipient');
    } else if (step === 'brand') {
      setStep('silo');
      setSelectedBrand(null);
    } else if (step === 'amount') {
      setStep('brand');
      setSelectedAmount(null);
    } else if (step === 'checkout') {
      setStep('amount');
    } else if (step === 'confirmation') {
      setRecipient('');
      setSelectedSilo(null);
      setSelectedBrand(null);
      setSelectedAmount(null);
      setMessage('');
      setStep('recipient');
    }
  };
  
  const goHome = () => {
    navigate('/');
    onBackToWelcome();
  };

  const handleSelectContact = (contact: Contact) => {
    setSelectedContact(contact);
    setRecipient(contact.email);
  };
  
  const renderStep = () => {
    switch (step) {
      case 'recipient':
        return (
          <Card>
            <CardHeader>
              <CardTitle>Add Recipient</CardTitle>
              <CardDescription>Who would you like to send a micro-gift to?</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {selectedContact ? (
                <div className="p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-givgo-light flex items-center justify-center">
                      {selectedContact.avatar ? (
                        <img src={selectedContact.avatar} alt={selectedContact.name} className="h-10 w-10 rounded-full" />
                      ) : (
                        <User className="h-5 w-5 text-givgo-purple" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium">{selectedContact.name}</p>
                      <p className="text-sm text-muted-foreground">{selectedContact.email}</p>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="ml-auto"
                      onClick={() => setShowContactsDialog(true)}
                    >
                      Change
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <Button 
                    className="w-full bg-givgo-purple hover:bg-givgo-purple/90"
                    onClick={() => setShowContactsDialog(true)}
                  >
                    Select from Contacts
                  </Button>
                  
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs">
                      <span className="bg-background px-2 text-muted-foreground">OR</span>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="recipient">Email or Phone Number</Label>
                    <Input 
                      id="recipient" 
                      placeholder="friend@example.com" 
                      value={recipient}
                      onChange={(e) => setRecipient(e.target.value)}
                    />
                  </div>
                </div>
              )}
              
              <Button 
                className="w-full bg-givgo-purple hover:bg-givgo-purple/90"
                onClick={handleContinue}
                disabled={!recipient}
              >
                <User className="mr-2 h-4 w-4" /> Continue
              </Button>
            </CardContent>
          </Card>
        );
        
      case 'silo':
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold">Select Your GivGo Magic!</h2>
            <ProductSilos onSelect={(silo) => {
              setSelectedSilo(silo);
              handleContinue();
            }} />
          </div>
        );
        
      case 'brand':
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-center">Select a Brand</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {BRANDS.filter(brand => brand.silo === selectedSilo).map(brand => (
                <div 
                  key={brand.id} 
                  className="cursor-pointer hover:scale-105 transition-transform duration-200" 
                  onClick={() => {
                    setSelectedBrand(brand);
                    handleContinue();
                  }}
                >
                  <GiftCard 
                    gift={{
                      id: 'preview',
                      brandId: brand.id,
                      amount: 5,
                      senderId: 'current-user',
                      recipientId: 'recipient',
                      createdAt: new Date(),
                      isRedeemed: false
                    }}
                    brand={brand}
                    hideAmount={true}
                    onSelect={() => {
                      setSelectedBrand(brand);
                      handleContinue();
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        );
        
      case 'amount':
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold">Choose Amount</h2>
            <div className="grid grid-cols-3 gap-4">
              {[5, 10, 15].map((amount) => (
                <Button 
                  key={amount}
                  variant={selectedAmount === amount ? "default" : "outline"} 
                  className={`h-16 text-xl ${selectedAmount === amount ? "bg-givgo-purple hover:bg-givgo-purple/90" : ""}`}
                  onClick={() => setSelectedAmount(amount as GiftAmount)}
                >
                  ${amount}
                </Button>
              ))}
            </div>
            
            <div className="space-y-2 pt-4">
              <Label htmlFor="message">Add a Message (Optional)</Label>
              <Textarea 
                id="message" 
                placeholder="You're awesome!" 
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </div>
            
            <Button 
              className="w-full bg-givgo-purple hover:bg-givgo-purple/90"
              onClick={handleContinue}
              disabled={!selectedAmount}
            >
              Continue
            </Button>
          </div>
        );
        
      case 'checkout':
        return (
          <Card>
            <CardHeader>
              <CardTitle>Checkout</CardTitle>
              <CardDescription>Review your GivGo Bucks and complete your purchase</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {selectedBrand && selectedAmount && (
                <GiftCard 
                  gift={{
                    id: 'preview',
                    brandId: selectedBrand.id,
                    amount: selectedAmount,
                    message: message,
                    senderId: 'current-user',
                    recipientId: recipient,
                    createdAt: new Date(),
                    isRedeemed: false
                  }}
                  brand={selectedBrand}
                />
              )}
              
              <div className="border-t pt-4">
                <div className="flex justify-between py-1">
                  <span>GivGo Bucks Amount</span>
                  <span>${selectedAmount?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-1">
                  <span>Transaction Fee</span>
                  <span>$0.99</span>
                </div>
                <div className="flex justify-between py-1 font-semibold">
                  <span>Total</span>
                  <span>${selectedAmount ? (selectedAmount + 0.99).toFixed(2) : '0.00'}</span>
                </div>
              </div>
              
              <Button 
                className="w-full bg-givgo-purple hover:bg-givgo-purple/90"
                onClick={handleContinue}
              >
                <Send className="mr-2 h-4 w-4" /> Complete Purchase
              </Button>
            </CardContent>
          </Card>
        );
        
      case 'confirmation':
        return (
          <div className="text-center space-y-6 py-6">
            <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center text-green-600">
              <Check className="h-8 w-8" />
            </div>
            <h2 className="text-2xl font-semibold">You just made someone's day!</h2>
            <p className="text-muted-foreground">
              Your GivGo Bucks have been sent to {recipient} and will be available to them immediately.
            </p>
            <Button
              className="bg-givgo-purple hover:bg-givgo-purple/90"
              onClick={() => onBackToWelcome()}
            >
              Send More GivGo Bucks
            </Button>
          </div>
        );
    }
  };
  
  return (
    <div className="max-w-md mx-auto animate-fade-in">
      {step !== 'confirmation' && step !== 'recipient' && (
        <div className="flex justify-between items-center mb-4">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={handleBack}
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Back
          </Button>
          
          <Button 
            variant="outline" 
            size="sm"
            onClick={goHome}
          >
            <Home className="mr-2 h-4 w-4" /> Home
          </Button>
        </div>
      )}
      
      {renderStep()}
      
      <ContactsSelectionDialog 
        open={showContactsDialog}
        onOpenChange={setShowContactsDialog}
        onSelectContact={handleSelectContact}
      />
    </div>
  );
};

export default GiverFlow;
